import React, { useEffect, useRef, useState } from "react";
import { RichCard } from "../../RichMessageTypes";
import {
  NavigationButton,
  Direction,
  NavigationButtonStyleType
} from "../navigationButton/NavigationButton";
import { DescriptionPairContent } from "./descriptionPair/DescriptionPairContent";
import { MediaContent } from "./media/MediaContent";
import styles from "./Carousel.css";
import { ButtonContent, ButtonsStyleType } from "../buttons/ButtonContent";

const showdown = require("showdown");
const converter = new showdown.Converter({
  simplifiedAutoLink: true,
  simpleLineBreaks: true,
  tables: true
});

interface Props {
  cards: RichCard[];
  disableTextButtons?: boolean;
  isMobile?: boolean;
}

export const Carousel = ({ cards, disableTextButtons, isMobile }: Props) => {
  // tslint:disable-next-line:no-null-keyword
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const CARD_WIDTH = 240;
  const TOTAL_BORDER = 2;
  const GAP = 5;
  const EFFECTIVE_CARD_WIDTH = CARD_WIDTH + TOTAL_BORDER + GAP;

  const scrollToIndex = (index: number) => {
    if (containerRef.current) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth;

      const scrollPosition =
        index * EFFECTIVE_CARD_WIDTH - containerWidth / 2 + CARD_WIDTH / 2 + TOTAL_BORDER / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth"
      });
    }
  };

  const scrollLeft = () => {
    if (containerRef.current) {
      const currentScrollLeft = containerRef.current.scrollLeft;
      const index = Math.max(0, Math.round(currentScrollLeft / EFFECTIVE_CARD_WIDTH) - 1);
      scrollToIndex(index);
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      const currentScrollLeft = containerRef.current.scrollLeft;
      const index = Math.round(currentScrollLeft / EFFECTIVE_CARD_WIDTH) + 1;
      scrollToIndex(index);
    }
  };

  const updateNavigationButton = (event?: Event) => {
    const t = (event ? event.target : containerRef.current) as HTMLElement;

    setCanScrollLeft(t.scrollLeft > 0);
    setCanScrollRight(Math.ceil(t.scrollLeft + t.clientWidth) < Math.floor(t.scrollWidth));
  };

  useEffect(() => {
    const ref = containerRef.current;
    if (ref) {
      ref.addEventListener("scroll", updateNavigationButton);
      updateNavigationButton();
      return () => {
        ref.removeEventListener("scroll", updateNavigationButton);
      };
    }
    return;
  }, []);

  return (
    <div className={styles.wrapper}>
      {!isMobile && (
        <NavigationButton
          direction={Direction.PREVIOUS}
          navigationButtonStyle={NavigationButtonStyleType.CAROUSEL}
          onClick={scrollLeft}
          hidden={!canScrollLeft}
        />
      )}
      <div className={styles.carousel} ref={containerRef}>
        {cards.map((richCard, index) => (
          <div key={index} className={styles.row}>
            <div className={styles["row-content"]}>
              {richCard.content ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: converter
                      .makeHtml(richCard.content)
                      .replace(/a href/g, "a target='_blank' href")
                  }}
                />
              ) : (
                <>
                  {richCard.media && <MediaContent media={richCard.media} />}
                  {richCard.descriptionPairs && richCard.descriptionPairs.length > 0 && (
                    <DescriptionPairContent descriptions={richCard.descriptionPairs} />
                  )}
                </>
              )}
              {richCard.actions && richCard.actions.length > 0 && (
                <ButtonContent
                  buttons={richCard.actions}
                  buttonsStyle={ButtonsStyleType.CAROUSEL}
                  disableTextButtons={disableTextButtons}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      {!isMobile && (
        <NavigationButton
          direction={Direction.NEXT}
          navigationButtonStyle={NavigationButtonStyleType.CAROUSEL}
          onClick={scrollRight}
          hidden={!canScrollRight}
        />
      )}
    </div>
  );
};
